/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    useState,
    memo,
    useEffect,
    useCallback,
    useRef,
    forwardRef,
    useImperativeHandle,
    useMemo,
    ChangeEvent,
} from 'react';
import {
    isNil,
    forEach,
    has,
    isBoolean,
    isObject,
    noop,
    isUndefined,
    isArray,
    isEmpty,
    isNumber,
    isEqual,
    first,
    isString,
} from 'lodash';
import { useTranslation } from 'react-i18next';
import conditionalClassNames from 'classnames';
// Import Types
import type {
    DataTableColumn,
    DropdownOption,
    ListHeaderBuiltInAction,
    ListHeaderConfig,
    ResolvedListHeaderAction,
    Sorting,
} from 'types';
import type { DateTableBulkAction, DataTableRefActions } from './types';
//  Import Services
import { UtilsHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import useDataTable from 'hooks/UseDataTable';
import useHasPermission from 'hooks/UseHasPermission';
import { useGTMEvent } from 'hooks/UseGTM';
// Import UI components
import { Button, ComboTable, Popover, Option, Checkbox, Radio, Tooltip, Icon, Search } from '@geneui/components';
import LabelsModal from 'components/LabelsModal';
import LabelTag from 'components/common/LabelTag';
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
import SelectedItemsModal from './SelectedItemsModal';
import ColumnsChooser from './ColumnsChooser';
import {
    ListHeaderActions,
    ListHeaderTitle,
    filterRowsByQuery,
    hasListHeaderActionAccess,
    resolveListHeaderActions,
} from '../ListHeader';
import { useFilterTagsContext } from '../FilterTag/FilterTagsContext';
// Import Constants
import { CustomTypes } from './constants';
import {
    l,
    LabelsModalViews,
    ActionsIcons,
    ModalsClassNames,
    SortDirectionTypes,
    DataTablePagingOptions,
    GACategoryEvents,
} from 'constants/common';
// Import Enums
import { ListHeaderActionPlacementEnum, ListHeaderActionTypeEnum } from 'enums';
// Import Styles
import 'assets/scss/dataTable.scss';
import styles from '../ListHeader/listHeader.module.scss';

const getDefaultPagingSize = (pagingOptions: Record<string, any>[]) => {
    return first(pagingOptions.filter(({ defaultSelected }) => defaultSelected === true))?.value;
};
const { GAFiltersCategory } = GACategoryEvents;
const emptyColumnConfig = {};

interface Props {
    rowKey: string;
    columnKey?: string;
    data: AnyObject[];
    dataCount?: Nullable<number>;
    rowActionBar?: Nilable<(row: AnyObject) => React.ReactNode>;
    columns: DataTableColumn[];
    radioSelectColumnConfig?: any;
    multiSelectColumnConfig?: any;
    isLoading?: boolean;
    tableKey?: string;
    name?: React.ReactNode;
    setPagingPageSize?: (size: number) => void;
    setPagingPageNumber?: (page: number) => void;
    receivedCurrentPageNumber?: number;
    receivedCurrentPageSize?: number;
    sorting?: Nullable<Partial<Sorting>>;
    setSorting?: (direction: string, name: string) => void;
    isColumnsSortable?: boolean;
    isColumnsResizable?: boolean;
    isColumnsDraggable?: boolean;
    withSearch?: boolean;
    hideSearchDropdown?: boolean;
    withPagination?: boolean;
    withPageSelector?: boolean;
    withOverlay?: boolean;
    isShowRefreshButton?: boolean;
    isShowVerticalDots?: boolean;
    verticalDotsContentList?: DropdownOption<any>[];
    verticalDotsFn?: (value: any) => void;
    isShowColumnChooser?: boolean;
    onRefreshClick?: () => void;
    classNames?: string;
    withManageLabels?: boolean;
    setLabelObjectIdKey?: any;
    labelNameKey?: any;
    labelColorKey?: any;
    labelObjectType?: any;
    isRowExpandable?: boolean;
    isVisibleOnlyDataTable?: boolean;
    isVisibleHeader?: boolean;
    rowToggleHandler?: (row: AnyObject) => void;
    renderRowNestedContent?: (row: AnyObject) => React.ReactNode;
    headerActions?: React.ReactNode;
    overlayContent?: React.ReactNode;
    onDoubleClick?: (row: AnyObject) => void;
    isHasRowMultiSelect?: boolean;
    isHasRowRadioSelect?: boolean;
    isHasBulkActions?: boolean;
    isHasArchiveActions?: boolean;
    isHasUnarchiveActions?: boolean;
    archiveSelectedRows?: (rows: AnyObject[]) => void;
    unarchiveSelectedRows?: (rows: AnyObject[]) => void;
    isHasDeleteAction?: boolean;
    deleteSelectedRows?: (rows: AnyObject[]) => void;
    getSelectedRowsList?: (rows: AnyObject[]) => void;
    getRadioSelectedRow?: (row: Nullable<AnyObject>) => void;
    defaultRadioSelectedRow?: any;
    isBackendPagination?: boolean;
    defaultMultiSelectedRows?: any[];
    getDragAcceptType?: (row: string) => void;
    CustomPreview?: React.ReactNode;
    TitleActions?: React.ReactNode;
    withQueryParams?: boolean;
    getTitle?: (row: any) => string;
    footerValues?: Record<string, React.ReactNode>;
    isSortControlled?: boolean;
    pagingOptions?: any[];
    customBulkActions?: DateTableBulkAction[];
    isHasLeftHeaderActions?: boolean;
    headerLeftActions?: React.ReactNode;
    withHeaderActions?: boolean;
    /**
     * Opt-in compact header: title + filter chips on the left, the configured actions on the right and a
     * bulk-actions row only while rows are selected. When set, `TitleActions`, `headerActions`, `withSearch`,
     * `isShowRefreshButton`, `isShowColumnChooser` and `isShowVerticalDots` are ignored in favour of `headerConfig.actions`.
     * The built-in `Search` action filters the current `data` client-side, so use it with client-side data sets only.
     */
    headerConfig?: ListHeaderConfig;
}

const DataTable = forwardRef<Nilable<DataTableRefActions>, Props>((props, ref) => {
    const { t } = useTranslation();
    const { doDeleteRequest } = useRequest();
    const { hasPermission, hasPartnerSettings, hasSubscriptionSection } = useHasPermission();
    const { logEvent } = useGTMEvent();

    const {
        getTitle,
        rowKey,
        columnKey,
        isColumnsSortable = false,
        isColumnsResizable = false,
        isColumnsDraggable = false,
        data,
        dataCount,
        rowActionBar,
        columns: allColumns,
        radioSelectColumnConfig = emptyColumnConfig,
        multiSelectColumnConfig = emptyColumnConfig,
        name,
        tableKey = 'DataTable',
        withSearch = false,
        hideSearchDropdown = true,
        withManageLabels = false,
        setLabelObjectIdKey,
        labelNameKey,
        labelColorKey,
        labelObjectType,
        withPagination = true,
        withPageSelector = true,
        withOverlay = false,
        overlayContent,
        isLoading,
        isShowRefreshButton = false,
        onRefreshClick,
        isShowColumnChooser = false,
        isShowVerticalDots = false,
        verticalDotsContentList = [],
        verticalDotsFn = noop,
        setPagingPageSize = noop,
        setPagingPageNumber = noop,
        receivedCurrentPageNumber = 1,
        receivedCurrentPageSize,
        sorting = null,
        setSorting = noop,
        classNames = '',
        isRowExpandable = false,
        isVisibleOnlyDataTable = false,
        isVisibleHeader = true,
        rowToggleHandler = noop,
        renderRowNestedContent,
        headerActions = null,
        isHasRowMultiSelect = false,
        isHasRowRadioSelect = false,
        isHasBulkActions = false,
        isHasArchiveActions = true,
        isHasUnarchiveActions = true,
        archiveSelectedRows = noop,
        unarchiveSelectedRows = noop,
        isHasDeleteAction = true,
        deleteSelectedRows = noop,
        getSelectedRowsList = noop,
        getRadioSelectedRow = noop,
        defaultRadioSelectedRow = null,
        isBackendPagination = true,
        defaultMultiSelectedRows = [],
        getDragAcceptType = noop,
        CustomPreview,
        TitleActions = <></>,
        withQueryParams = false,
        footerValues,
        isSortControlled = false,
        pagingOptions = DataTablePagingOptions,
        customBulkActions,
        isHasLeftHeaderActions,
        headerLeftActions,
        withHeaderActions = true,
        headerConfig,
    } = props;

    const filterTagsContext = useFilterTagsContext();

    // Compact header derivations must precede the `isDataTableReady` state below: a config-declared
    // columns chooser has to hold the table until the saved columns are loaded, exactly like `isShowColumnChooser`.
    const isCompactHeader = !isNil(headerConfig);
    // Only actions that will actually render count: same visibility + access filter as `resolveListHeaderActions`.
    const compactActionTypes = useMemo(
        () =>
            new Set(
                headerConfig?.actions
                    .filter(
                        (action) =>
                            action.isHidden !== true &&
                            hasListHeaderActionAccess(action, { hasPermission, hasPartnerSettings }),
                    )
                    .map(({ type }) => type) ?? [],
            ),
        [headerConfig, hasPermission, hasPartnerSettings],
    );
    const hasColumnsChooser = isCompactHeader
        ? compactActionTypes.has(ListHeaderActionTypeEnum.ColumnsChooser)
        : isShowColumnChooser;
    const hasLocalSearch = isCompactHeader && compactActionTypes.has(ListHeaderActionTypeEnum.Search);

    const rootRef = useRef<HTMLDivElement>(null);

    const { deleteObjectTagRequest } = useCancelableRequests({
        deleteObjectTagRequest: UtilsHttpService.deleteObjectTag,
    });

    // default page size comes from the table's own paging options, so custom options can mark their default;
    // falls back to the global options default when the custom options do not mark one
    const initialPageSize =
        receivedCurrentPageSize ?? getDefaultPagingSize(pagingOptions) ?? getDefaultPagingSize(DataTablePagingOptions);

    const dataTableData = useDataTable(
        receivedCurrentPageNumber,
        initialPageSize,
        sorting?.Name,
        sorting?.Direction,
        withQueryParams,
        tableKey,
    );

    const [isModalOpened, setModalOpenedState] = useState(false);
    const [isVisibleVerticalDotsContent, setIsVisibleVerticalDotsContent] = useState(false);
    const [verticalDotsContentSelectedOption, setVerticalDotsContentSelectedOption] = useState<Nilable<number>>();

    const [multiSelectedRows, setMultiSelectedRows] = useState<any[]>([]);
    const [multiSelectedRowsCount, setMultiSelectedRowsCount] = useState(0);

    const [radioSelectedRow, setRadioSelectedRow] = useState<Nullable<Record<string, any>>>(null);

    const [isAllVisibleRowsSelected, setIsAllVisibleRowsSelected] = useState(false);
    const [isIndeterminateVisibleRowsSelected, setIsIndeterminateVisibleRowsSelected] = useState(false);

    const [isVisibleLabelsModal, setIsVisibleLabelsModal] = useState(false);
    const [labelsModalView, setLabelsModalView] = useState('');
    const [labelObjectId, setLabelObjectId] = useState<Nilable<number>>();
    const [selectedLabelId, setSelectedLabelId] = useState<Nilable<number>>();
    const [isVisibleDeleteLabelModal, setIsVisibleDeleteLabelModal] = useState(false);

    const [isVisibleOverlay, setIsVisibleOverlay] = useState(false);
    const [filteredColumns, setFilteredColumns] = useState<any[]>([]);

    const [isDataTableReady, setIsDataTableReady] = useState(!hasColumnsChooser);
    const [isVisibleSelectedModal, setIsVisibleSelectedModal] = useState(false);
    const [deleteLabelObjectId, setDeleteLabelObjectId] = useState();
    const [isDeleteLabelBtnLoading, setIsDeleteLabelBtnLoading] = useState(false);
    const [deleteLabelName, setDeleteLabelName] = useState('');
    const [deleteLabelTitle, setDeleteLabelTitle] = useState('');

    const [remeasureTick, setRemeasureTick] = useState(0);
    const hasRemeasuredRef = useRef(false);
    const [searchQuery, setSearchQuery] = useState('');

    const columns = useMemo(() => {
        return allColumns.filter((column) => {
            if (column.subscriptionSection) {
                return hasSubscriptionSection(column.subscriptionSection);
            }

            if (column.mandatoryPermissions) {
                return hasPermission(column.mandatoryPermissions, true);
            }

            if (column.permissions) {
                return hasPermission(column.permissions, false);
            }

            if (column.partnerSettings) {
                return column.partnerSettings.some((setting) => hasPartnerSettings(setting));
            }

            return true;
        });
    }, [allColumns, hasPartnerSettings, hasPermission, hasSubscriptionSection]);

    const searchDataKeys = useMemo(
        () =>
            columns
                .map(({ dataKey }) => dataKey)
                .filter((dataKey): dataKey is string => isString(dataKey) && !isEmpty(dataKey)),
        [columns],
    );

    // Manage labels logic
    const openLabelsModal = (view: keyof typeof LabelsModalViews, labelObjectId?: number, selectedLabelId?: number) => {
        setLabelsModalView(view);
        setIsVisibleLabelsModal(true);
        setLabelObjectId(labelObjectId);
        setSelectedLabelId(selectedLabelId);
    };

    const handleDeleteLabel = (objectId, columnKey, title) => {
        setDeleteLabelTitle(title);
        setDeleteLabelName(columnKey);
        setIsVisibleDeleteLabelModal(true);
        setDeleteLabelObjectId(objectId);
    };

    const labelsModalCloseHandler = (isRefreshTableData) => {
        setIsVisibleLabelsModal(false);
        if (isRefreshTableData === true) {
            onRefreshClick?.();
        }
    };

    const comparatorSelectedRows = useCallback(
        (row1, row2) => {
            return isEqual(row1?.data[rowKey], row2?.data[rowKey]);
        },
        [rowKey],
    );

    const hasInMultiSelectedRows = useCallback(
        (row) => {
            return multiSelectedRows.reduce((acc, item) => (acc = acc || comparatorSelectedRows(item, row)), false);
        },
        [multiSelectedRows, comparatorSelectedRows],
    );

    const getMultiSelectedRow = useCallback(
        (row) => {
            return multiSelectedRows.filter((item) => comparatorSelectedRows(item, row))[0];
        },
        [multiSelectedRows, comparatorSelectedRows],
    );

    // Row multi select logic
    const rowSelectionToggleHandler = useCallback(
        (event, row) => {
            const { checked } = event.currentTarget;
            if (checked) {
                setMultiSelectedRows([...multiSelectedRows, row]);
            } else {
                const selectedRow = getMultiSelectedRow(row);
                setMultiSelectedRows(multiSelectedRows.filter((item) => !comparatorSelectedRows(item, selectedRow)));
            }
        },
        [multiSelectedRows, getMultiSelectedRow, comparatorSelectedRows],
    );

    const rowRadioSelectionToggleHandler = useCallback(
        (_event, row) => {
            if (!isNil(radioSelectedRow)) {
                radioSelectedRow.data.isRowSelected = false;
                radioSelectedRow.data.isManuallySelected = false;
            }
            row.data.isRowSelected = true;
            row.data.isManuallySelected = true;
            setRadioSelectedRow(row);
            if (withOverlay) {
                setIsVisibleOverlay(true);
            }
        },
        [radioSelectedRow, withOverlay],
    );

    const setAllHandler = () => {
        const resultLength = multiSelectedRows.length;

        let tmpIndeterminate = false;
        const tmpData = data?.filter((row) => !row.isUnselectable);

        const tmpIsVisibleRowsSelected = tmpData.reduce(
            (acc, item) => {
                const tmpHasInMultiselected = hasInMultiSelectedRows(item);
                tmpIndeterminate = tmpIndeterminate || tmpHasInMultiselected;
                return (acc = acc && tmpHasInMultiselected);
            },
            resultLength !== 0 && tmpData.length !== 0,
        );

        setIsAllVisibleRowsSelected(tmpIsVisibleRowsSelected);
        setIsIndeterminateVisibleRowsSelected(!tmpIsVisibleRowsSelected && tmpIndeterminate);
    };

    useEffect(() => {
        setMultiSelectedRowsCount(multiSelectedRows.length);
        getSelectedRowsList?.(multiSelectedRows);
        setAllHandler();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [multiSelectedRows]);

    useEffect(() => {
        getRadioSelectedRow?.(radioSelectedRow);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [radioSelectedRow]);

    useEffect(() => {
        if (isHasRowMultiSelect) {
            setAllHandler();
        } else if (isHasRowRadioSelect) {
            data.forEach((row) => {
                if (!isNil(radioSelectedRow) && comparatorSelectedRows(radioSelectedRow, row)) {
                    row.data.isRowSelected = true;
                    setRadioSelectedRow(row);
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const allRowsSelectionToggleHandler = useCallback(
        (event: ChangeEvent<HTMLInputElement>, isSelected?: boolean) => {
            const checked = isBoolean(isSelected) ? isSelected : event.currentTarget.checked;

            setIsAllVisibleRowsSelected(checked);

            const newPushedMultiSelectedRows: Record<string, any>[] = [];
            const newPulledMultiSelectedRowKeys: string[] = [];
            const tmpData = data?.filter((row) => !row.isUnselectable);

            forEach(tmpData, (row) => {
                if (checked) {
                    if (!hasInMultiSelectedRows(row)) {
                        newPushedMultiSelectedRows.push(row);
                    }
                } else {
                    const selectedRow = getMultiSelectedRow(row);
                    newPulledMultiSelectedRowKeys.push(selectedRow.data[rowKey]);
                }
            });
            setMultiSelectedRows(
                multiSelectedRows
                    .filter((item) => !newPulledMultiSelectedRowKeys.includes(item.data[rowKey]))
                    .concat(newPushedMultiSelectedRows),
            );
        },
        [data, multiSelectedRows, rowKey, hasInMultiSelectedRows, getMultiSelectedRow],
    );

    const selectedRowClickHandler = () => {
        setIsVisibleSelectedModal(true);
    };

    const bulkActionClickHandler = (bulkAction) => {
        bulkAction.onClick(multiSelectedRows);
    };

    const bulkActions: DateTableBulkAction[] = [];

    if (multiSelectedRowsCount > 0 && isHasDeleteAction) {
        bulkActions.push({
            icon: 'bc-icon-delete-2',
            text: t(l.Delete),
            onClick: deleteSelectedRows,
        });
    }

    if (multiSelectedRowsCount > 0 && isHasArchiveActions) {
        bulkActions.push({
            icon: 'bc-icon-archive',
            text: t(l.Archive),
            onClick: archiveSelectedRows,
        });
    }

    if (multiSelectedRowsCount > 0 && isHasUnarchiveActions) {
        bulkActions.push({
            icon: 'bc-icon-unarchive',
            text: t(l.Unarchive),
            onClick: unarchiveSelectedRows,
        });
    }

    if (multiSelectedRowsCount > 0 && !isEmpty(customBulkActions)) {
        // TODO: `concat` returns a new array that is discarded, so custom bulk actions never render.
        // Kept as-is on purpose (behaviour change tracked in a follow-up ticket).
        bulkActions.concat(customBulkActions!);
    }

    const getHeaderBulkActions = () => {
        if (isHasBulkActions !== true) return;

        return (
            multiSelectedRowsCount > 0 && (
                <div className="crm-data-table-header-bulk-actions">
                    {bulkActions.map((bulkAction, index) => (
                        <Button
                            key={index}
                            onClick={() => bulkActionClickHandler(bulkAction)}
                            icon={bulkAction.icon}
                            appearance="outline"
                            color="primary"
                            itemsDirection="start"
                        >
                            {bulkAction.text}
                        </Button>
                    ))}

                    <Button
                        onClick={selectedRowClickHandler}
                        appearance="minimal"
                        color="primary"
                        itemsDirection="start"
                        className="crm-data-table-header-selected"
                    >
                        {`${multiSelectedRowsCount} ${t(l.Selected)}`}
                    </Button>
                </div>
            )
        );
    };

    const resetSelectedRows = () => {
        setIsVisibleOverlay(false);
    };

    // Header actions bar
    const getLabelsActionBtn = () => {
        return (
            <Button
                appearance="minimal"
                icon="bc-icon-category"
                onClick={() => openLabelsModal(LabelsModalViews.manageLabelsView)}
            >
                {t(l.ManageLabels)}
            </Button>
        );
    };

    const getCombinedHeaderActions = () => {
        return (
            <>
                <div className="crm-data-table-header-action-bar-left-actions">
                    {withManageLabels === true && getLabelsActionBtn()}
                    {isHasRowMultiSelect === true && getHeaderBulkActions()}
                    {isHasLeftHeaderActions === true && headerLeftActions}
                </div>
                {headerActions && <div className="crm-data-table-header-action-bar-right-actions">{headerActions}</div>}
            </>
        );
    };

    const addSortingFunctionInColumns = (columns) => {
        columns.forEach((column) => {
            if (column.sortable === true && !isNil(column.type)) {
                if (!isNil(CustomTypes[column.type])) {
                    column.sortFn = CustomTypes[column.type].sortFn;
                }
            }
        });
    };

    const getColumns = useCallback(
        (fromColumnsChooser = false, receivedColumns = [...columns], fromSelectedItemsModal = false) => {
            if (!isDataTableReady && !fromColumnsChooser) {
                return [];
            }

            let clonedColumns = receivedColumns;
            if (!fromSelectedItemsModal === true) {
                if (
                    withManageLabels === true &&
                    !isUndefined(setLabelObjectIdKey) &&
                    !isUndefined(labelColorKey) &&
                    !isUndefined(labelNameKey)
                ) {
                    clonedColumns = [
                        {
                            text: t(l.Labels),
                            dataKey: labelNameKey,
                            sortable: false,
                            colRenderer: (columnKey, _index, row) => {
                                const labelColor = row.data[labelColorKey];
                                const objectId = row.data[setLabelObjectIdKey];
                                const categoryId = row.data['CategoryId'];
                                const title = getTitle?.(row);
                                return (
                                    <div className="set-category">
                                        {!isNil(columnKey) ? (
                                            <LabelTag
                                                color={labelColor}
                                                labelName={columnKey}
                                                onClick={() =>
                                                    openLabelsModal(LabelsModalViews.setLabelView, objectId, categoryId)
                                                }
                                                icon={
                                                    <button
                                                        className="remove-btn"
                                                        onClick={() => handleDeleteLabel(objectId, columnKey, title)}
                                                    >
                                                        <Icon type="bc-icon-is-not" />
                                                    </button>
                                                }
                                            />
                                        ) : (
                                            <button
                                                className="set-category-text"
                                                onClick={() => openLabelsModal(LabelsModalViews.setLabelView, objectId)}
                                            >
                                                {t(l.SetLabel)}
                                            </button>
                                        )}
                                    </div>
                                );
                            },
                        },
                        ...clonedColumns,
                    ];
                }
            }

            if (!fromColumnsChooser && !isEmpty(filteredColumns)) {
                const tmpColumns = clonedColumns.reduce((acc, item) => {
                    acc[item.dataKey] = item;
                    return acc;
                }, {});
                clonedColumns = [];
                filteredColumns.forEach((item) => {
                    if (!isNil(tmpColumns[item])) {
                        clonedColumns.push(tmpColumns[item]);
                    }
                });
            }

            addSortingFunctionInColumns(clonedColumns);

            if (!fromSelectedItemsModal === true) {
                if (isHasRowMultiSelect === true) {
                    // if (first(clonedColumns).dataKey !== columnKey) {
                    clonedColumns = [
                        {
                            width: 50,
                            render: () => (
                                <Checkbox
                                    checked={isAllVisibleRowsSelected}
                                    indeterminate={isIndeterminateVisibleRowsSelected}
                                    onChange={(e) => {
                                        allRowsSelectionToggleHandler(e);
                                    }}
                                />
                            ),
                            dataKey: columnKey,
                            sortable: false,
                            hasOptions: false,
                            resizable: false,
                            draggable: false,
                            colRenderer: (_columnKey, _index, row) => {
                                return (
                                    <Checkbox
                                        disabled={row.isUnselectable}
                                        checked={hasInMultiSelectedRows(row)}
                                        onChange={(e) => {
                                            rowSelectionToggleHandler(e, row);
                                        }}
                                    />
                                );
                            },
                            ...multiSelectColumnConfig,
                        },
                        ...clonedColumns,
                    ];
                } else {
                    if (isHasRowRadioSelect === true) {
                        clonedColumns = [
                            {
                                width: 50,
                                render: () => <></>,
                                dataKey: columnKey,
                                sortable: false,
                                hasOptions: false,
                                resizable: false,
                                draggable: false,
                                colRenderer: (_columnKey, _index, row) => {
                                    return (
                                        <Radio
                                            disabled={row.isDisabled}
                                            checked={row.data.isRowSelected}
                                            onChange={(e) => {
                                                rowRadioSelectionToggleHandler(e, row);
                                            }}
                                        />
                                    );
                                },
                                ...radioSelectColumnConfig,
                            },
                            ...clonedColumns,
                        ];
                    }
                }
            }

            if (isEmpty(clonedColumns)) {
                clonedColumns = [...columns];
            }

            return clonedColumns;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            columns,
            isDataTableReady,
            withManageLabels,
            setLabelObjectIdKey,
            labelColorKey,
            labelNameKey,
            getTitle,
            isHasRowMultiSelect,
            t,
            columnKey,
            isAllVisibleRowsSelected,
            isIndeterminateVisibleRowsSelected,
            allRowsSelectionToggleHandler,
            hasInMultiSelectedRows,
            rowSelectionToggleHandler,
            isHasRowRadioSelect,
            rowRadioSelectionToggleHandler,
            multiSelectColumnConfig,
            radioSelectColumnConfig,
            filteredColumns,
        ],
    );

    // ComboTable re-seeds its internal column state (order, widths, autosize) whenever the
    // columns array identity changes, so it has to stay stable across renders.
    const tableColumns = useMemo(() => getColumns(), [getColumns]);
    const columnsChooserColumns = useMemo(() => getColumns(true), [getColumns]);

    const getRows = () => {
        if (!isDataTableReady) {
            return [];
        }

        return hasLocalSearch ? filterRowsByQuery(data, searchQuery, searchDataKeys) : data;
    };

    const onPaginationChange = (page: number) => {
        resetSelectedRows();
        dataTableData.setData({ pageNumber: page });
    };

    const onSelectorChange = (count: number | 'all') => {
        resetSelectedRows();
        dataTableData.setData({ pageNumber: 1, pageSize: count === 'all' ? 10000 : count });
    };

    // TODO implement backend sorting functional
    const onSortingChange = (_isSortable, columnName, _index, _dataKey, _element, sortingDirection) => {
        resetSelectedRows();
        dataTableData.setData({ sortingName: columnName, sortingDirection: sortingDirection });
    };

    // Title actions bar logic
    const verticalDotsClickHandler = (value) => {
        setIsVisibleVerticalDotsContent(false);
        if ((isNumber(value) || isNil(value)) && verticalDotsContentSelectedOption !== value) {
            verticalDotsFn?.(value);
            setVerticalDotsContentSelectedOption(value);
        }
    };

    const deleteObjectTag = (objectId, labelObjectType) => {
        setIsDeleteLabelBtnLoading(true);
        doDeleteRequest(deleteObjectTagRequest.request, {
            queryString: { id: objectId, objectType: labelObjectType },
            successCallback: () => {
                onRefreshClick?.();
                setIsDeleteLabelBtnLoading(false);
                setIsVisibleDeleteLabelModal(false);
            },
        });
    };

    const handleColumnsChooserOpen = () => {
        logEvent(GAFiltersCategory.name, GAFiltersCategory.events[`${tableKey}Column`], tableKey, 1);
        setModalOpenedState(true);
    };

    const getTitleActions = () => {
        return (
            <>
                {TitleActions}
                {isShowColumnChooser && (
                    <Tooltip text={t(l.ColumnsChooser)}>
                        <Button
                            onClick={handleColumnsChooserOpen}
                            icon="bc-icon-settings"
                            appearance="minimal"
                            color="primary"
                        />
                    </Tooltip>
                )}
                {isShowRefreshButton && (
                    <Tooltip text={t(l.ReloadData)}>
                        <Button onClick={onRefreshClick} icon="bc-icon-reset" appearance="minimal" color="primary" />
                    </Tooltip>
                )}
                {isShowVerticalDots && (
                    <Popover
                        toggleHandler={() => setIsVisibleVerticalDotsContent(!isVisibleVerticalDotsContent)}
                        isOpen={isVisibleVerticalDotsContent}
                        extendTargetWidth={false}
                        className="crm-data-table-top-action-bar-popover"
                        position="bottom"
                        align="end"
                        maxHeight={120}
                        Content={
                            <>
                                {verticalDotsContentList?.map(({ label, value }, index) => (
                                    <Option
                                        key={index}
                                        title={t(label)}
                                        onClick={() => verticalDotsClickHandler(value)}
                                        active={value === verticalDotsContentSelectedOption}
                                    />
                                ))}
                            </>
                        }
                        scrollbarNeeded={true}
                    >
                        <Tooltip text={t(l.Mode)}>
                            <Button
                                onClick={verticalDotsClickHandler}
                                icon="bc-icon-more-vertical"
                                appearance="minimal"
                                color="primary"
                            />
                        </Tooltip>
                    </Popover>
                )}
            </>
        );
    };

    // Compact header logic (`headerConfig`)
    const handleSearchChange = (_event: ChangeEvent<HTMLInputElement>, value: string) => {
        setSearchQuery(value);
    };

    const getBuiltInHeaderAction = (
        action: ListHeaderBuiltInAction,
        placement: ListHeaderActionPlacementEnum,
    ): Nullable<ResolvedListHeaderAction> => {
        const { id, type, tooltip, label, icon, isDisabled = false } = action;

        const toResolvedAction = (
            defaultLabel: string,
            defaultIcon: string,
            onClick: () => void,
            isActionDisabled = isDisabled,
        ): ResolvedListHeaderAction =>
            placement === ListHeaderActionPlacementEnum.Menu
                ? {
                      id,
                      placement,
                      menuItem: {
                          label: label ?? defaultLabel,
                          icon: icon ?? defaultIcon,
                          onClick,
                          isDisabled: isActionDisabled,
                      },
                  }
                : {
                      id,
                      placement,
                      headerNode: (
                          <Tooltip text={tooltip ?? defaultLabel}>
                              <Button
                                  icon={icon ?? defaultIcon}
                                  appearance="minimal"
                                  color="primary"
                                  disabled={isActionDisabled}
                                  onClick={onClick}
                              />
                          </Tooltip>
                      ),
                  };

        switch (type) {
            case ListHeaderActionTypeEnum.Refresh:
                return toResolvedAction(t(l.ReloadData), 'bc-icon-reset', () => onRefreshClick?.());
            case ListHeaderActionTypeEnum.ColumnsChooser:
                return toResolvedAction(t(l.ColumnsChooser), 'bc-icon-settings', handleColumnsChooserOpen);
            case ListHeaderActionTypeEnum.ManageLabels:
                return toResolvedAction(t(l.ManageLabels), 'bc-icon-category', () =>
                    openLabelsModal(LabelsModalViews.manageLabelsView),
                );
            case ListHeaderActionTypeEnum.SaveFilters:
                if (isNil(filterTagsContext)) {
                    console.warn(`DataTable: the "${type}" header action needs a FilterTagsProvider above the table.`);
                    return null;
                }

                return toResolvedAction(
                    t(l.SaveFilters),
                    'bc-icon-save',
                    filterTagsContext.openSaveFiltersModal,
                    isDisabled || filterTagsContext.isSaveDisabled,
                );
            case ListHeaderActionTypeEnum.SavedFilters:
                if (isNil(filterTagsContext)) {
                    console.warn(`DataTable: the "${type}" header action needs a FilterTagsProvider above the table.`);
                    return null;
                }

                return toResolvedAction(
                    t(l.SavedFilters),
                    'bc-icon-settings-bars',
                    filterTagsContext.openSavedFiltersModal,
                    isDisabled || filterTagsContext.isDisabled,
                );
            case ListHeaderActionTypeEnum.Search:
                return {
                    id,
                    placement: ListHeaderActionPlacementEnum.Header,
                    headerNode: (
                        <Search
                            className={styles.listHeaderSearch}
                            placeholder={tooltip ?? t(l.Search)}
                            defaultValue={searchQuery}
                            onChange={handleSearchChange}
                        />
                    ),
                };
            default:
                return null;
        }
    };

    const resolvedHeaderActions = isNil(headerConfig)
        ? []
        : resolveListHeaderActions({
              actions: headerConfig.actions,
              hasPermission,
              hasPartnerSettings,
              getBuiltInAction: getBuiltInHeaderAction,
          });

    const getCompactName = () => (
        <ListHeaderTitle
            title={name}
            chips={headerConfig?.shouldShowFilterChips === false ? [] : filterTagsContext?.chips}
            onChipRemove={filterTagsContext?.removeChip}
        />
    );

    const getCompactTitleActions = () => (
        <ListHeaderActions
            actions={resolvedHeaderActions}
            menuTooltip={headerConfig?.menuTooltip}
            menuIcon={headerConfig?.menuIcon}
        />
    );

    // The bulk-actions row is the only content of gene-ui's second header row in compact mode,
    // so passing `undefined` while nothing is selected removes that row entirely.
    const getCompactHeaderActions = () => {
        if (isHasRowMultiSelect && isHasBulkActions && multiSelectedRowsCount > 0) {
            return getHeaderBulkActions();
        }

        return headerConfig?.legacyHeaderActions;
    };

    const getExpandIconDisableState = (row) => {
        return !(has(row, 'isRowExpandable') && row.isRowExpandable);
    };

    const resetDataTable = () => {
        resetSelectedRows();
        dataTableData.setData({ pageNumber: 1 });
    };

    const changePaginationPageNumberDataTable = (pageNumber = 1) => {
        resetSelectedRows();
        dataTableData.setData({ pageNumber: pageNumber });
    };

    const onSaveColumns = (newColumns) => {
        !isDataTableReady && setIsDataTableReady(true);
        setFilteredColumns(newColumns);
    };

    const SetRadioSelectedRow = (newRow) => {
        if (!isNil(radioSelectedRow)) {
            radioSelectedRow.data.isRowSelected = false;
            radioSelectedRow.data.isManuallySelected = false;
        }
        setRadioSelectedRow(newRow);
        data.forEach((row) => {
            if (comparatorSelectedRows(row, newRow)) {
                row.data.isRowSelected = true;
                setRadioSelectedRow(row);
                if (withOverlay) {
                    setIsVisibleOverlay(true);
                }
            }
        });
    };

    useEffect(() => {
        if (isHasRowRadioSelect && !isNil(defaultRadioSelectedRow) && isNil(radioSelectedRow)) {
            SetRadioSelectedRow(defaultRadioSelectedRow);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultRadioSelectedRow]);

    const init = () => {
        if (
            isArray(verticalDotsContentList) &&
            isObject(verticalDotsContentList[0]) &&
            has(verticalDotsContentList[0], 'value')
        ) {
            setVerticalDotsContentSelectedOption(verticalDotsContentList[0].value);
        }
        if (!isNil(defaultMultiSelectedRows)) {
            setMultiSelectedRows(defaultMultiSelectedRows);
        }
    };

    const clickOverlayHandler = () => {
        if (isHasRowRadioSelect && isNil(radioSelectedRow) && !isVisibleOverlay) {
            return;
        }
        setIsVisibleOverlay(!isVisibleOverlay);
    };

    const backendPagingProps: {
        selectorValue?: Nullable<number>;
        currentPage?: number;
        onPaginationChange?: (page: number) => void;
        onSelectorChange?: (count: number | 'all') => void;
    } = {};

    if (isBackendPagination) {
        backendPagingProps.selectorValue = withPageSelector === true ? dataTableData.data.pageSize : dataCount;
        backendPagingProps.currentPage = dataTableData.data.pageNumber;
        backendPagingProps.onPaginationChange = onPaginationChange;
        backendPagingProps.onSelectorChange = onSelectorChange;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    useEffect(() => {
        const node = rootRef.current;
        if (isNil(node) || typeof ResizeObserver === 'undefined') {
            return;
        }

        let prevWidth = node.offsetWidth;
        let rafId = 0;
        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                const width = node.offsetWidth;
                if (prevWidth === 0 && width > 0 && !hasRemeasuredRef.current) {
                    hasRemeasuredRef.current = true;
                    setRemeasureTick((tick) => tick + 1);
                }
                prevWidth = width;
            });
        });

        observer.observe(node);

        return () => {
            cancelAnimationFrame(rafId);
            observer.disconnect();
        };
    }, []);

    useImperativeHandle(ref, () => ({
        reset() {
            resetDataTable();
        },
        resetSelectedRows() {
            setMultiSelectedRows([]);
            if (!isNil(radioSelectedRow)) {
                radioSelectedRow.data.isRowSelected = false;
                radioSelectedRow.data.isManuallySelected = false;
                if (withOverlay) {
                    setIsVisibleOverlay(false);
                }
            }
            setRadioSelectedRow(null);
        },
        setRadioSelectedRow(row) {
            SetRadioSelectedRow(row);
        },
        changePaginationPageNumber(pageNumber) {
            changePaginationPageNumberDataTable(pageNumber);
        },
        changeTableConfig(data) {
            dataTableData.setData(data);
        },
        changeDefaultSorting(data) {
            dataTableData.changeDefaultSorting(data);
        },
    }));

    useEffect(() => {
        const data = dataTableData.data;
        setPagingPageSize?.(data.pageSize);
        setPagingPageNumber?.(data.pageNumber);
        setSorting?.(data.sortingDirection, data.sortingName);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataTableData.data]);

    const dynamicProps = isSortControlled
        ? {
              handleColumnSorting(column) {
                  resetSelectedRows();
                  const sortConfig = {
                      sortingName: column?.dataKey,
                      sortingDirection:
                          column?.dataKey === dataTableData.data.sortingName
                              ? dataTableData.data.sortingDirection === SortDirectionTypes.ASC
                                  ? null
                                  : SortDirectionTypes.ASC
                              : SortDirectionTypes.DESC,
                  };
                  dataTableData.setData(sortConfig);
              },
          }
        : {};

    const rows = getRows();
    const isLocalSearchActive = hasLocalSearch && searchQuery.trim() !== '';

    return (
        <div
            ref={rootRef}
            className={conditionalClassNames(
                `crm-common-table crm-data-table c-table-styles crm-data-table-header-action-bar ${classNames}`,
                {
                    'crm-data-table-hide-header-left-actions-bar': !withSearch,
                    'crm-data-table-hide-expand-icon': !isRowExpandable,
                    'crm-data-table-hide-header-footer': isVisibleOnlyDataTable,
                    'crm-data-table-hide-header': !isVisibleHeader,
                    'crm-data-table-hide-footer': !withPageSelector && !withPagination,
                    [styles.compactHeader]: isCompactHeader,
                },
            )}
        >
            <ComboTable
                key={remeasureTick}
                name={isCompactHeader ? getCompactName() : name}
                rowKey={rowKey}
                columnKey={columnKey}
                sortableColumns={isColumnsSortable}
                resizableColumns={isColumnsResizable}
                draggableColumns={isColumnsDraggable}
                columns={tableColumns}
                onSortChange={onSortingChange}
                rows={rows}
                rowsCount={isLocalSearchActive ? rows.length : dataCount}
                rowActionBar={rowActionBar}
                getExpandIconDisableState={getExpandIconDisableState}
                withPagination={withPagination}
                withPageSelector={withPageSelector}
                selectorData={pagingOptions}
                {...backendPagingProps}
                withSearch={withSearch && !isCompactHeader}
                hideSearchDropdown={hideSearchDropdown}
                loading={isLoading || !isDataTableReady}
                titleActions={isCompactHeader ? getCompactTitleActions() : getTitleActions()}
                onRefreshClick={onRefreshClick}
                renderRowNestedChildren={renderRowNestedContent}
                rowToggleHandler={rowToggleHandler}
                headerActions={
                    isCompactHeader
                        ? getCompactHeaderActions()
                        : withHeaderActions
                          ? getCombinedHeaderActions()
                          : undefined
                }
                getDragAcceptType={getDragAcceptType}
                CustomPreview={CustomPreview}
                defaultSortedColumn={dataTableData.data.sortingName}
                defaultSortType={dataTableData.data.sortingDirection}
                sortedColumn={dataTableData.data.sortingName}
                sortType={dataTableData.data.sortingDirection}
                footerValues={footerValues}
                {...dynamicProps}
            />

            {hasColumnsChooser && (
                <ColumnsChooser
                    tableName={tableKey ?? ''}
                    modalOpenedState={isModalOpened}
                    onSave={onSaveColumns}
                    setModalOpenedState={setModalOpenedState}
                    possibleColumns={columnsChooserColumns}
                />
            )}

            {labelsModalView && isVisibleLabelsModal && (
                <LabelsModal
                    view={labelsModalView}
                    isVisible={isVisibleLabelsModal}
                    objectType={labelObjectType}
                    handleClose={labelsModalCloseHandler}
                    objectId={labelObjectId}
                    selectedLabelId={selectedLabelId}
                />
            )}
            {withOverlay && (
                <div className={`overlay-content-wrap ${isVisibleOverlay ? 'overlay-clear' : 'overlay-left'}`}>
                    <Button
                        icon={isVisibleOverlay ? 'bc-icon-clear' : 'bc-icon-arrow-left'}
                        appearance="minimal"
                        color="primary"
                        itemsDirection="start"
                        onClick={clickOverlayHandler}
                        className="overlay-button"
                    />
                    <div>{overlayContent}</div>
                </div>
            )}

            {isVisibleSelectedModal && (
                <SelectedItemsModal
                    rows={multiSelectedRows}
                    columns={getColumns(false, [...columns], true)}
                    handleClose={() => setIsVisibleSelectedModal(false)}
                    handleSave={setMultiSelectedRows}
                    isVisible={isVisibleSelectedModal}
                    rowKey={rowKey}
                    columnKey={columnKey}
                />
            )}

            {isVisibleDeleteLabelModal && (
                <ConfirmationModal
                    onOk={() => deleteObjectTag(deleteLabelObjectId, labelObjectType)}
                    onCancel={() => setIsVisibleDeleteLabelModal(false)}
                    isVisibleModal={isVisibleDeleteLabelModal}
                    titleText={t(l.RemoveLabel)}
                    questionLabel={t(l.AreYouSureWantToRemoveLabel, {
                        labelName: `"${deleteLabelName}"`,
                        title: `"${deleteLabelTitle}"`,
                        listName: `${
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            isString(name) || isNumber(name) || isBoolean(name) ? name : (name as any)?.props?.children
                        }`,
                    })}
                    isLoading={isDeleteLabelBtnLoading}
                    iconType={ActionsIcons.Delete}
                    actionLabel={t(l.Delete)}
                    className={ModalsClassNames.Delete}
                />
            )}
        </div>
    );
});

DataTable.displayName = 'DataTable';

export default memo(DataTable);
